<template>
  <div
    class="relative w-full max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8 text-slate-900 dark:text-[#F1F5F9] font-sans antialiased min-h-full flex flex-col justify-between bg-transparent dark:bg-[#0B1120]"
  >
    <div>
      <!-- Header -->
      <header class="border-b border-slate-200/80 dark:border-[#263449] pb-5 mb-6">
        <div
          class="flex items-center gap-2 text-indigo-600 dark:text-blue-400 font-semibold text-xs tracking-wider uppercase mb-1"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
            />
          </svg>
          <span>Appearance Settings</span>
        </div>
        <h1
          class="text-xl sm:text-3xl font-extrabold text-slate-900 dark:text-[#F1F5F9] tracking-tight"
        >
          Website Visual Themes
        </h1>
        <p
          class="mt-1 text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] leading-relaxed max-w-3xl"
        >
          Choose a visual design identity for your school portal. Select a theme
          below to preview its color palette and component aesthetics.
        </p>
      </header>

      <!-- Loading State -->
      <div
        v-if="isWebsiteLoading"
        class="py-16 flex items-center justify-center"
      >
        <AppLoader
          variant="card"
          size="md"
          message="Fetching Website Configuration..."
          subtext="Loading color palettes, typography, and theme presets"
          color="indigo"
        />
      </div>

      <!-- Main Form & Scrollable Themes -->
      <form
        v-else
        @submit.prevent="handleSaveTheme"
        class="space-y-6 pb-28"
      >
        <!-- Scrollable Theme Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div
            v-for="theme in themes"
            :key="theme.id"
            @click="selectedTemplate = theme.id"
            :class="[
              'group relative cursor-pointer rounded-2xl border-2 p-4 sm:p-6 transition-all duration-300 flex flex-col justify-between overflow-hidden',
              selectedTemplate === theme.id
                ? 'border-indigo-600 ring-4 ring-indigo-600/10 bg-gradient-to-b from-indigo-50/20 to-white shadow-md'
                : 'border-slate-200/90 hover:border-slate-300 bg-white hover:shadow-md hover:-translate-y-0.5',
            ]"
          >
            <!-- Selected Glow Accent -->
            <div
              v-if="selectedTemplate === theme.id"
              class="absolute -right-12 -top-12 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none"
            ></div>

            <!-- Active Check Indicator -->
            <div
              v-if="selectedTemplate === theme.id"
              class="absolute top-4 right-4 bg-indigo-600 text-white rounded-full p-1.5 shadow-md shadow-indigo-600/30 animate-in zoom-in-50 duration-200"
            >
              <svg
                class="w-3.5 h-3.5 sm:w-4 sm:h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <div>
              <!-- Header Info -->
              <div class="flex items-center space-x-2 pr-8">
                <span
                  class="text-[10px] sm:text-xs font-mono font-bold px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-700 uppercase tracking-wider"
                >
                  {{ theme.code }}
                </span>
                <h3
                  class="text-base sm:text-lg font-bold text-slate-900 tracking-tight"
                >
                  {{ theme.name }}
                </h3>
              </div>

              <p class="mt-2 text-xs text-slate-600 leading-relaxed">
                {{ theme.description }}
              </p>

              <!-- Color Palette Swatches -->
              <div class="mt-5 space-y-2">
                <div class="flex items-center justify-between">
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider"
                  >
                    Color Palette
                  </span>
                </div>

                <div class="grid grid-cols-5 gap-1.5 sm:gap-2">
                  <div
                    v-for="(color, idx) in theme.palette"
                    :key="idx"
                    class="group/swatch relative h-7 sm:h-8 rounded-lg shadow-inner transition-transform duration-200 hover:scale-105 border border-black/5"
                    :style="{ backgroundColor: color.hex }"
                  >
                    <!-- Tooltip -->
                    <span
                      class="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover/swatch:opacity-100 transition-all duration-150 bg-slate-900 text-white text-[10px] font-medium px-2 py-0.5 rounded shadow-lg whitespace-nowrap pointer-events-none z-20"
                    >
                      {{ color.label }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Component Mini-Preview Mockup -->
            <div class="mt-5 pt-4 border-t border-slate-100">
              <div
                class="rounded-xl p-3 text-xs flex items-center justify-between shadow-xs transition-colors duration-200"
                :style="{
                  backgroundColor: theme.previewBg,
                  color: theme.previewText,
                }"
              >
                <div class="flex items-center space-x-2 min-w-0">
                  <div
                    class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: theme.palette[2]?.hex }"
                  ></div>
                  <span class="font-medium text-xs truncate"
                    >Sample Header Banner</span
                  >
                </div>
                <span
                  class="px-2.5 py-1 rounded-md text-[10px] font-bold flex-shrink-0 ml-2 shadow-xs"
                  :style="{
                    backgroundColor: theme.palette[2]?.hex,
                    color: '#FFFFFF',
                  }"
                >
                  Action
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- COMPACT FLOATING ACTION BAR (STICKY TO BOTTOM RIGHT ALWAYS) -->
        <teleport to="body">
          <div
            class="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-8 z-[900] max-w-[calc(100vw-2rem)] sm:max-w-lg pointer-events-auto"
          >
            <div
              class="rounded-2xl bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl border border-slate-200/90 dark:border-[#263449] p-2.5 sm:p-3 shadow-2xl shadow-slate-900/15 flex items-center justify-between gap-3 sm:gap-4 ring-1 ring-slate-950/5 dark:ring-white/5"
            >
              <!-- Status Indicator -->
              <div class="flex items-center gap-2 text-xs font-medium min-w-0">
                <div
                  class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  :class="
                    isSuccess
                      ? 'bg-emerald-500 ring-4 ring-emerald-500/20'
                      : hasChanges
                      ? 'bg-amber-500 animate-pulse'
                      : 'bg-slate-300 dark:bg-slate-700'
                  "
                ></div>
                <div class="text-xs text-slate-600 dark:text-[#A6B4C8] truncate">
                  Selected Theme:
                  <span
                    class="font-bold text-slate-900 dark:text-[#F1F5F9] uppercase tracking-wider ml-1"
                  >
                    {{ selectedTemplate }}
                  </span>
                </div>
              </div>

              <!-- Action Button with Spinner & Success State -->
              <button
                type="submit"
                :disabled="isSavingActive || (!hasChanges && !isSuccess)"
                :class="[
                  'h-10 px-4 sm:px-6 rounded-xl text-xs font-bold shadow-md transition-all duration-200 inline-flex items-center justify-center gap-2 whitespace-nowrap flex-shrink-0 active:scale-[0.98]',
                  isSuccess
                    ? 'bg-emerald-600 text-white shadow-emerald-600/25'
                    : isSavingActive
                    ? 'bg-indigo-600 text-white shadow-indigo-600/25 opacity-90 cursor-wait'
                    : hasChanges
                    ? 'bg-indigo-600 text-white shadow-indigo-600/25 hover:bg-indigo-500 cursor-pointer focus:ring-2 focus:ring-indigo-500/40'
                    : 'bg-slate-100 dark:bg-[#18243A] text-slate-400 dark:text-[#7E8DA5] cursor-not-allowed border border-slate-200/60 dark:border-[#263449] shadow-none',
                ]"
              >
                <!-- Loading Spinner (Active while saving) -->
                <svg
                  v-if="isSavingActive"
                  class="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>

                <!-- Success Check Icon -->
                <svg
                  v-else-if="isSuccess"
                  class="w-4 h-4 text-white animate-in zoom-in duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M5 13l4 4L19 7"
                  />
                </svg>

                <!-- Button Label -->
                <span>
                  {{
                    isSavingActive
                      ? "Saving..."
                      : isSuccess
                      ? "Saved!"
                      : "Save Theme"
                  }}
                </span>
              </button>
            </div>
          </div>
        </teleport>
      </form>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted } from "vue";
  import { useStore } from "vuex";

  export default {
    name: "ThemeSettings",
    setup() {
      const store = useStore();

      const selectedTemplate = ref("default");
      const initialTemplate = ref("default");
      const isSaving = ref(false);
      const isSuccess = ref(false);

      const themes = [
        {
          id: "default",
          code: "classic",
          name: "The School Diary",
          description:
            "A nostalgic, exercise-book inspired design featuring deep navy ink, crisp cream paper, and warm gold accents.",
          previewBg: "#1B2A4A",
          previewText: "#F6F2E7",
          palette: [
            { label: "Ink Navy", hex: "#1B2A4A" },
            { label: "Cream Paper", hex: "#F6F2E7" },
            { label: "Margin Red", hex: "#B23A34" },
            { label: "Teal Accent", hex: "#1F6F64" },
            { label: "Gold Stamp", hex: "#C99A3B" },
          ],
        },
        {
          id: "template1",
          code: "eco",
          name: "Botanical Campus",
          description:
            "A fresh, natural identity with forest emerald tones, leafy rule lines, and terracotta margin details.",
          previewBg: "#1C3A27",
          previewText: "#F4F6F0",
          palette: [
            { label: "Forest Green", hex: "#1C3A27" },
            { label: "Mint Paper", hex: "#F4F6F0" },
            { label: "Terracotta", hex: "#D96B27" },
            { label: "Emerald", hex: "#2E7D5B" },
            { label: "Amber Gold", hex: "#E2B041" },
          ],
        },
        {
          id: "template2",
          code: "royal",
          name: "Royal Academy",
          description:
            "Regal and distinguished style driven by deep plum midnight, blush ivory paper, and champagne gold trim.",
          previewBg: "#2E1A33",
          previewText: "#FAF6F8",
          palette: [
            { label: "Plum Midnight", hex: "#2E1A33" },
            { label: "Blush Ivory", hex: "#FAF6F8" },
            { label: "Royal Crimson", hex: "#8B2648" },
            { label: "Imperial Violet", hex: "#6D326D" },
            { label: "Champagne Gold", hex: "#D4A359" },
          ],
        },
        {
          id: "template3",
          code: "stem",
          name: "Modern STEM",
          description:
            "A high-tech slate design featuring vibrant electric indigo highlights, digital paper, and cyan elements.",
          previewBg: "#0F172A",
          previewText: "#F8FAFC",
          palette: [
            { label: "Slate Navy", hex: "#0F172A" },
            { label: "Digital White", hex: "#F8FAFC" },
            { label: "Electric Indigo", hex: "#4F46E5" },
            { label: "Modern Cyan", hex: "#0284C7" },
            { label: "Amber Accent", hex: "#F59E0B" },
          ],
        },
        {
          id: "template4",
          code: "earth",
          name: "Sunset Terracotta",
          description:
            "Warm earthy rust tones, soft sand paper background, sage green secondary accents, and deep charcoal ink.",
          previewBg: "#2D2421",
          previewText: "#FAF7F2",
          palette: [
            { label: "Earthy Espresso", hex: "#2D2421" },
            { label: "Sand Paper", hex: "#FAF7F2" },
            { label: "Terracotta Rust", hex: "#C85A32" },
            { label: "Sage Green", hex: "#4A7C59" },
            { label: "Warm Gold", hex: "#D99B26" },
          ],
        },
        {
          id: "template5",
          code: "nordic",
          name: "Nordic Slate & Amber",
          description:
            "Scandinavian minimalism with deep charcoal slate, cool grey canvas, electric amber highlights, and teal accents.",
          previewBg: "#1E293B",
          previewText: "#F1F5F9",
          palette: [
            { label: "Dark Slate", hex: "#1E293B" },
            { label: "Cool Grey", hex: "#F1F5F9" },
            { label: "Electric Amber", hex: "#D97706" },
            { label: "Nordic Teal", hex: "#0D9488" },
            { label: "Bright Amber", hex: "#F59E0B" },
          ],
        },
      ];

      const isWebsiteLoading = computed(
        () => store.state.isWebsiteLoading || false,
      );
      const isWebsiteSaving = computed(
        () => store.state.isWebsiteSaving || false,
      );
      const schoolData = computed(() => store.state.websiteDetails || {});

      // Combines local and store saving state
      const isSavingActive = computed(
        () => isSaving.value || isWebsiteSaving.value,
      );

      const hasChanges = computed(
        () => selectedTemplate.value !== initialTemplate.value,
      );

      onMounted(async () => {
        try {
          const data = await store.dispatch("fetchWebsiteDetails");
          const currentTheme =
            data?.school?.template || schoolData.value?.template || "default";
          selectedTemplate.value = currentTheme;
          initialTemplate.value = currentTheme;
        } catch (err) {
          console.error("Failed to load initial website details:", err);
        }
      });

      const handleSaveTheme = async () => {
        if (!hasChanges.value || isSavingActive.value) return;

        isSaving.value = true;
        isSuccess.value = false;

        try {
          const payload = {
            template: selectedTemplate.value,
          };

          await store.dispatch("updateWebsiteDetails", payload);
          await store.dispatch("fetchWebsiteDetails");
          initialTemplate.value = selectedTemplate.value;

          // Show success state briefly
          isSuccess.value = true;
          setTimeout(() => {
            isSuccess.value = false;
          }, 2200);
        } catch (err) {
          console.error("Error saving theme setting:", err);
        } finally {
          isSaving.value = false;
        }
      };

      return {
        themes,
        selectedTemplate,
        isWebsiteLoading,
        isSavingActive,
        isSuccess,
        hasChanges,
        handleSaveTheme,
      };
    },
  };
</script>
